import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  Snackbar,
  Alert,
  InputAdornment,
  MenuItem,
  CircularProgress,
  Divider,
  Stack,
  Chip,
  alpha,
  Autocomplete,
  IconButton,
  Tooltip,
  Collapse,
} from "@mui/material";
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Clear as ClearIcon,
  AttachMoney as MoneyIcon,
  LocalGasStation as FuelIcon,
  Build as RepairIcon,
  TireRepair as TireIcon,
  Receipt as ReceiptIcon,
  CloudUpload as CloudUploadIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Autorenew as ReplaceIcon,
  AddCircleOutline as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AttachFile as AttachFileIcon,
} from "@mui/icons-material";
import {
  createExpense,
  updateExpense,
  getExpenseDetails,
  vehicleList,
} from "../../../Services/Services";

// ─── Constants ───────────────────────────────────────────────────
import { brandColor as brand } from "../../../theme";

// ─── Shared TextField Style (matches AddNewUser / VehicleForm) ────
const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "4px",
    backgroundColor: "#fff",
    transition: "all 0.2s ease",
    "& fieldset": {
      borderColor: "#e5e7eb",
    },
    "&:hover fieldset": {
      borderColor: "#c7d2fe",
    },
    "&.Mui-focused fieldset": {
      borderColor: brand,
      borderWidth: 1.5,
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.78rem",
    color: "#6b7280",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: brand,
  },
  "& .MuiFormHelperText-root": {
    fontSize: "0.72rem",
    marginLeft: "4px",
  },
};

const expenseTypes = [
  {
    value: "diesel",
    label: "Diesel / Fuel",
    icon: <FuelIcon sx={{ fontSize: 16 }} />,
  },
  { value: "tyre", label: "Tyre", icon: <TireIcon sx={{ fontSize: 16 }} /> },
  {
    value: "repair",
    label: "Repair",
    icon: <RepairIcon sx={{ fontSize: 16 }} />,
  },
  {
    value: "service",
    label: "Scheduled Service",
    icon: <RepairIcon sx={{ fontSize: 16 }} />,
  },
  {
    value: "rto",
    label: "RTO / Permit",
    icon: <ReceiptIcon sx={{ fontSize: 16 }} />,
  },
  {
    value: "insurance",
    label: "Insurance",
    icon: <ReceiptIcon sx={{ fontSize: 16 }} />,
  },
  {
    value: "tax",
    label: "Road Tax",
    icon: <MoneyIcon sx={{ fontSize: 16 }} />,
  },
  {
    value: "toll_tax",
    label: "Toll Tax",
    icon: <MoneyIcon sx={{ fontSize: 16 }} />,
  },
  {
    value: "food",
    label: "Food / Driver Expense",
    icon: <MoneyIcon sx={{ fontSize: 16 }} />,
  },
  {
    value: "other",
    label: "Other Expense",
    icon: <MoneyIcon sx={{ fontSize: 16 }} />,
  },
];

const expenseTypeMap = expenseTypes.reduce((acc, t) => {
  acc[t.value] = t;
  return acc;
}, {});

const paymentModes = ["Cash", "Cheque", "UPI", "Bank Transfer", "Credit Card"];

const createEmptyLineItem = () => ({
  local_id: `li-${Date.now()}-${Math.random()}`,
  line_item_id: null,
  expense_type: "",
  amount: "",
  km_reading: "",
  description: "",
  vendor_name: "",
  payment_mode: "Cash",
  receipt_no: "",
  next_service_km: "",
  next_service_date: "",
  part_replaced: "",
  labour_charge: "",
  serviceBills: [],
  expenseBills: [],
  existingServiceBills: [],
  existingExpenseBills: [],
});

// ── Document Card Component (compact) ──
const DocumentCard = ({
  title,
  type,
  lineItemId,
  existing,
  newBills,
  onUpload,
  onRemove,
  onView,
}) => {
  const hasFiles = existing.length > 0 || newBills.length > 0;

  const getFileIcon = (file) => {
    if (file.type === "application/pdf" || file.name?.endsWith(".pdf"))
      return <PdfIcon sx={{ color: "#dc2626", fontSize: 15 }} />;
    if (file.type?.startsWith("image/"))
      return <ImageIcon sx={{ color: brand, fontSize: 15 }} />;
    return <DescriptionIcon sx={{ color: "#64748b", fontSize: 15 }} />;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.25,
        borderRadius: "4px",
        border: hasFiles ? "1.5px solid #10b981" : "1.5px dashed #d1d5db",
        bgcolor: hasFiles ? alpha("#10b981", 0.04) : "#f8fafc",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: hasFiles ? 0.75 : 0.5,
        }}
      >
        <Typography
          sx={{ fontWeight: 600, fontSize: "0.68rem", color: "#374151" }}
        >
          {title}
        </Typography>
        {hasFiles && (
          <Chip
            label={newBills.length > 0 ? "New" : "Saved"}
            size="small"
            sx={{
              borderRadius: "4px",
              bgcolor:
                newBills.length > 0 ? alpha(brand, 0.1) : alpha("#10b981", 0.1),
              color: newBills.length > 0 ? brand : "#10b981",
              fontSize: "0.58rem",
              fontWeight: 600,
              height: 16,
              px: 0.5,
            }}
          />
        )}
      </Box>

      {!hasFiles ? (
        <Button
          component="label"
          variant="outlined"
          startIcon={<CloudUploadIcon sx={{ fontSize: 13 }} />}
          size="small"
          fullWidth
          sx={{
            borderRadius: "4px",
            textTransform: "none",
            fontSize: "0.68rem",
            py: 0.5,
            borderColor: "#c7d2fe",
            color: brand,
            bgcolor: alpha(brand, 0.04),
            boxShadow: "none",
            "&:hover": {
              borderColor: brand,
              bgcolor: alpha(brand, 0.09),
              boxShadow: "none",
            },
          }}
        >
          Choose File
          <input
            type="file"
            hidden
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => onUpload(lineItemId, type, e)}
          />
        </Button>
      ) : (
        <Stack spacing={0.5}>
          {existing.map((doc) => (
            <Box
              key={doc.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                bgcolor: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "4px",
                px: 0.75,
                py: 0.4,
              }}
            >
              {getFileIcon(doc)}
              <Typography
                sx={{
                  flex: 1,
                  fontSize: "0.65rem",
                  color: "#374151",
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {doc.url?.split("/").pop() || doc.name}
              </Typography>
              <Tooltip title="View document">
                <IconButton
                  size="small"
                  onClick={() => onView(doc.url, doc.name)}
                  sx={{ p: 0.3, color: brand }}
                >
                  <ViewIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Replace file">
                <IconButton
                  size="small"
                  component="label"
                  sx={{ p: 0.3, color: "#6b7280", "&:hover": { color: brand } }}
                >
                  <ReplaceIcon sx={{ fontSize: 13 }} />
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => onUpload(lineItemId, type, e)}
                  />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove">
                <IconButton
                  size="small"
                  onClick={() => onRemove(lineItemId, type, doc.id, true)}
                  sx={{ p: 0.3, color: "#dc2626" }}
                >
                  <DeleteIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
            </Box>
          ))}

          {newBills.map((bill) => (
            <Box
              key={bill.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                bgcolor: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "4px",
                px: 0.75,
                py: 0.4,
              }}
            >
              {getFileIcon(bill)}
              <Typography
                sx={{
                  flex: 1,
                  fontSize: "0.65rem",
                  color: "#374151",
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {bill.name}
              </Typography>
              <Tooltip title="Replace file">
                <IconButton
                  size="small"
                  component="label"
                  sx={{ p: 0.3, color: "#6b7280", "&:hover": { color: brand } }}
                >
                  <ReplaceIcon sx={{ fontSize: 13 }} />
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => onUpload(lineItemId, type, e)}
                  />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove">
                <IconButton
                  size="small"
                  onClick={() => onRemove(lineItemId, type, bill.id)}
                  sx={{ p: 0.3, color: "#dc2626" }}
                >
                  <DeleteIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Stack>
      )}
    </Paper>
  );
};

const ExpenseForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(isEditMode);
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);

  const [voucherData, setVoucherData] = useState({
    voucher_id: null,
    vehicle_number: "",
    expense_date: new Date().toISOString().split("T")[0],
    remarks: "",
  });

  const [lineItems, setLineItems] = useState([createEmptyLineItem()]);
  // Which line item is expanded for editing. Only one open at a time — accordion style.
  const [expandedId, setExpandedId] = useState(lineItems[0].local_id);

  const [voucherErrors, setVoucherErrors] = useState({});
  const [errors, setErrors] = useState({});

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchVehicles = async () => {
    setLoadingVehicles(true);
    try {
      const response = await vehicleList("", "", 1);
      let vehicles = [];
      if (response?.data) vehicles = response.data.results || [];
      else if (response?.results) vehicles = response.results || [];
      setVehicleOptions(vehicles);
    } catch (error) {
      console.error("Failed to fetch vehicles:", error);
    } finally {
      setLoadingVehicles(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
    if (isEditMode && id) fetchExpenseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode]);

  const fetchExpenseData = async () => {
    try {
      setPageLoading(true);
      const response = await getExpenseDetails(id);
      if (response) {
        setVoucherData({
          voucher_id: response.voucher_id || response.expense_id,
          vehicle_number: response.vehicle_number || "",
          expense_date:
            response.expense_date ||
            response.voucher_date ||
            new Date().toISOString().split("T")[0],
          remarks: response.remarks || "",
        });

        const rawItems =
          response.line_items && response.line_items.length > 0
            ? response.line_items
            : [response];

        const filledItems = rawItems.map((item) => {
          const base = createEmptyLineItem();
         const filled = {
            ...base,
            line_item_id: item.line_item_id || item.expense_id || null,
            expense_type: item.expense_type || "",
            amount: item.amount || "",
            km_reading: item.km_reading || "",
            description: item.description || "",
            vendor_name: item.vendor_name || "",
            payment_mode: item.payment_mode || "Cash",
            receipt_no: item.receipt_no || "",
            next_service_km: item.next_service_km || "",
            next_service_date: item.next_service_date || "",
            part_replaced: item.part_replaced || "",
            labour_charge: item.labour_charge || "",
          };
          if (item.service_bill_url) {
            filled.existingServiceBills = [
              {
                id: `svc-${filled.local_id}`,
                url: item.service_bill_url,
                type: item.service_bill_url.endsWith(".pdf") ? "pdf" : "image",
              },
            ];
          }
          if (item.expense_bill_url) {
            filled.existingExpenseBills = [
              {
                id: `exp-${filled.local_id}`,
                url: item.expense_bill_url,
                type: item.expense_bill_url.endsWith(".pdf") ? "pdf" : "image",
              },
            ];
          }
          return filled;
        });

        setLineItems(filledItems);
        // Collapse everything by default when editing — user expands what they want to check.
        setExpandedId(null);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to fetch expense data",
        severity: "error",
      });
    } finally {
      setPageLoading(false);
    }
  };

  const handleVoucherChange = (e) => {
    const { name, value } = e.target;
    setVoucherData((prev) => ({ ...prev, [name]: value }));
    if (voucherErrors[name])
      setVoucherErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleVehicleSelect = (event, newValue) => {
    if (newValue) {
      const vehicleNumber =
        newValue.vehicle_number || newValue.vehicleNumber || newValue;
      setVoucherData((prev) => ({
        ...prev,
        vehicle_number:
          typeof vehicleNumber === "string" ? vehicleNumber : vehicleNumber,
      }));
    } else {
      setVoucherData((prev) => ({ ...prev, vehicle_number: "" }));
    }
  };

  const handleVehicleInputChange = (event, newInputValue) => {
    setVoucherData((prev) => ({ ...prev, vehicle_number: newInputValue }));
  };

  const toggleExpand = (local_id) => {
    setExpandedId((prev) => (prev === local_id ? null : local_id));
  };

  const addLineItem = () => {
    const newItem = createEmptyLineItem();
    setLineItems((prev) => [...prev, newItem]);
    setExpandedId(newItem.local_id); // auto-expand the new row, collapse others
  };

  const removeLineItem = (local_id) => {
    setLineItems((prev) => {
      if (prev.length === 1) return prev;
      const target = prev.find((li) => li.local_id === local_id);
      [
        ...(target?.serviceBills || []),
        ...(target?.expenseBills || []),
      ].forEach((b) => b.preview && URL.revokeObjectURL(b.preview));
      return prev.filter((li) => li.local_id !== local_id);
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next[local_id];
      return next;
    });
    setExpandedId((prev) => (prev === local_id ? null : prev));
  };

  const handleLineItemChange = (local_id, e) => {
    const { name, value } = e.target;
    setLineItems((prev) =>
      prev.map((li) =>
        li.local_id === local_id ? { ...li, [name]: value } : li,
      ),
    );
    setErrors((prev) => {
      if (!prev[local_id]?.[name]) return prev;
      return { ...prev, [local_id]: { ...prev[local_id], [name]: "" } };
    });
  };

  const handleLineItemFileUpload = (local_id, type, e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    files.forEach((file) => {
      const isValidType =
        file.type === "application/pdf" || file.type.startsWith("image/");
      const isValidSize = file.size <= 5 * 1024 * 1024;
      if (isValidType && isValidSize) {
        validFiles.push({
          id: Date.now() + Math.random(),
          file,
          name: file.name,
          type: file.type,
          preview: file.type.startsWith("image/")
            ? URL.createObjectURL(file)
            : null,
        });
      }
    });

    if (validFiles.length !== files.length) {
      setSnackbar({
        open: true,
        message: "Some files invalid. Only PDF/Image under 5MB allowed.",
        severity: "error",
      });
    }

    const key = type === "service" ? "serviceBills" : "expenseBills";
    setLineItems((prev) =>
      prev.map((li) => {
        if (li.local_id !== local_id) return li;
        li[key].forEach((b) => b.preview && URL.revokeObjectURL(b.preview));
        return { ...li, [key]: validFiles.slice(0, 1) };
      }),
    );
    e.target.value = null;
  };

  const handleLineItemRemoveFile = (
    local_id,
    type,
    fileId,
    isExisting = false,
  ) => {
    const key = isExisting
      ? type === "service"
        ? "existingServiceBills"
        : "existingExpenseBills"
      : type === "service"
        ? "serviceBills"
        : "expenseBills";
    setLineItems((prev) =>
      prev.map((li) =>
        li.local_id === local_id
          ? { ...li, [key]: li[key].filter((b) => b.id !== fileId) }
          : li,
      ),
    );
  };

  const handleViewFile = (url) => window.open(url, "_blank");

  const totalAmount = lineItems.reduce(
    (sum, li) => sum + (parseFloat(li.amount) || 0),
    0,
  );
  const totalBillCount = (li) =>
    li.existingServiceBills.length +
    li.existingExpenseBills.length +
    li.serviceBills.length +
    li.expenseBills.length;

  const validate = () => {
    const newVoucherErrors = {};
    if (!voucherData.vehicle_number)
      newVoucherErrors.vehicle_number = "Vehicle number is required";
    if (!voucherData.expense_date)
      newVoucherErrors.expense_date = "Date is required";

    const newErrors = {};
    lineItems.forEach((li) => {
      const itemErrors = {};
      if (!li.expense_type)
        itemErrors.expense_type = "Expense type is required";
      if (!li.amount)
        itemErrors.amount = "Amount is required";
      if (Object.keys(itemErrors).length) newErrors[li.local_id] = itemErrors;
    });

    return { newVoucherErrors, newErrors };
  };

  const handleSubmit = async () => {
    const { newVoucherErrors, newErrors } = validate();
    if (
      Object.keys(newVoucherErrors).length > 0 ||
      Object.keys(newErrors).length > 0
    ) {
      setVoucherErrors(newVoucherErrors);
      setErrors(newErrors);
      // Expand the first invalid row so the user sees what to fix.
      const firstInvalid = lineItems.find((li) => newErrors[li.local_id]);
      if (firstInvalid) setExpandedId(firstInvalid.local_id);
      return;
    }
    setLoading(true);
    try {
      const submitData = new FormData();
      submitData.append("expense_date", voucherData.expense_date);
      submitData.append("vehicle_number", voucherData.vehicle_number);
      if (voucherData.remarks)
        submitData.append("remarks", voucherData.remarks);
      if (voucherData.voucher_id)
        submitData.append("voucher_id", voucherData.voucher_id);

      const lineItemsMeta = lineItems.map((li, idx) => {
        const {
          serviceBills,
          expenseBills,
          existingServiceBills,
          existingExpenseBills,
          local_id,
          ...meta
        } = li;

        // Empty strings aren't valid for DRF numeric/date fields — send null instead
        ["km_reading", "next_service_km", "labour_charge"].forEach((field) => {
          if (meta[field] === "" || meta[field] === undefined)
            meta[field] = null;
        });
        if (
          meta.next_service_date === "" ||
          meta.next_service_date === undefined
        ) {
          meta.next_service_date = null;
        }

        if (serviceBills[0])
          submitData.append(
            `line_item_${idx}_service_bill`,
            serviceBills[0].file,
          );
        if (expenseBills[0])
          submitData.append(
            `line_item_${idx}_expense_bill`,
            expenseBills[0].file,
          );
        return meta;
      });
      submitData.append("line_items", JSON.stringify(lineItemsMeta));

      if (isEditMode) await updateExpense(id, submitData);
      else await createExpense(submitData);

      setSnackbar({
        open: true,
        message: `Expense ${isEditMode ? "updated" : "added"} successfully!`,
        severity: "success",
      });
      setTimeout(() => navigate("/dashboard/ExpensesList"), 1500);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Something went wrong",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClearAll = () => {
    setVoucherData({
      voucher_id: null,
      vehicle_number: "",
      expense_date: new Date().toISOString().split("T")[0],
      remarks: "",
    });

    const first = createEmptyLineItem();
    setLineItems([first]);
    setExpandedId(first.local_id);
    setErrors({});
    setVoucherErrors({});
  };

  if (pageLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress sx={{ color: brand }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{ px: 0, pt: 0, mt: 0, maxWidth: 1200, mx: "auto", width: "100%" }}
    >
      <Paper
        sx={{
          borderRadius: "4px",
          border: "1px solid #e5e7eb",
          boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        {/* ── Header ── */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            backgroundColor: "#f8fafc",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "4px",
                  bgcolor: alpha(brand, 0.1),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ReceiptIcon sx={{ color: brand, fontSize: 18 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#111827",
                    fontSize: "1rem",
                    lineHeight: 1.2,
                    textAlign: "left",
                  }}
                >
                  {isEditMode ? "Edit Expense" : "Add New Expense"}
                </Typography>
                <Typography
                  sx={{ fontSize: "0.7rem", color: "#6b7280", mt: 0.2 }}
                >
                  {isEditMode
                    ? `Editing Expense ID: ${id}`
                    : "Fill in the details below to record an expense"}
                </Typography>
              </Box>
              {isEditMode && (
                <Chip
                  label={`ID: ${id}`}
                  size="small"
                  sx={{
                    borderRadius: "4px",
                    bgcolor: alpha(brand, 0.1),
                    color: brand,
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    height: 20,
                  }}
                />
              )}
            </Stack>

            <Button
              startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
              onClick={() => navigate("/dashboard/ExpensesList")}
              size="small"
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "0.75rem",
                px: 2,
                py: 0.75,
                borderColor: "#d1d5db",
                border: "1px solid #d1d5db",
                color: "#6b7280",
                boxShadow: "none",
                "&:hover": {
                  borderColor: brand,
                  color: brand,
                  bgcolor: alpha(brand, 0.04),
                  boxShadow: "none",
                },
              }}
            >
              Back to List
            </Button>
          </Box>
        </Box>

        {/* ── Form Content ── */}
        <Box sx={{ p: 2.5 }}>
          {/* ── Section: Voucher Details ── */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Box
              sx={{ width: 3, height: 18, bgcolor: brand, borderRadius: "4px" }}
            />
            <Typography
              sx={{ fontWeight: 700, color: "#111827", fontSize: "0.85rem" }}
            >
              Vehicle & Trip Details
            </Typography>
          </Stack>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                fullWidth
                freeSolo
                options={vehicleOptions}
                loading={loadingVehicles}
                getOptionLabel={(option) => {
                  if (typeof option === "string") return option;
                  return option.vehicle_number || option.vehicleNumber || "";
                }}
                value={voucherData.vehicle_number}
                onChange={handleVehicleSelect}
                onInputChange={handleVehicleInputChange}
                sx={{
                  "& .MuiAutocomplete-inputRoot": {
                    minHeight: "40px",
                    padding: "0px 8px",
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Vehicle Number"
                    name="vehicle_number"
                    size="small"
                    placeholder="Select or type"
                    error={!!voucherErrors.vehicle_number}
                    helperText={voucherErrors.vehicle_number}
                    sx={{
                      ...textFieldSx,
                      "& .MuiOutlinedInput-root": {
                        ...textFieldSx["& .MuiOutlinedInput-root"],
                        height: "44px",
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingVehicles ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                required
                type="date"
                label="Expense Date"
                name="expense_date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={voucherData.expense_date}
                onChange={handleVoucherChange}
                error={!!voucherErrors.expense_date}
                helperText={voucherErrors.expense_date}
                sx={textFieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={4}>
              <TextField
                fullWidth
                label="Remarks"
                name="remarks"
                size="small"
                placeholder="Any voucher-level remarks..."
                value={voucherData.remarks}
                onChange={handleVoucherChange}
                sx={textFieldSx}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2.5, borderColor: "#e5e7eb" }} />

          {/* ── Section: Expense Line Items ── */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1.5 }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 3,
                  height: 18,
                  bgcolor: brand,
                  borderRadius: "4px",
                }}
              />
              <Typography
                sx={{ fontWeight: 700, color: "#111827", fontSize: "0.85rem" }}
              >
                Expenses for this Vehicle
              </Typography>
              <Chip
                label={`${lineItems.length} item${lineItems.length > 1 ? "s" : ""}`}
                size="small"
                sx={{
                  borderRadius: "4px",
                  bgcolor: "#f1f5f9",
                  color: "#64748b",
                  fontSize: "0.6rem",
                  fontWeight: 600,
                  height: 18,
                }}
              />
            </Stack>
            <Typography
              sx={{ fontWeight: 700, fontSize: "0.8rem", color: brand }}
            >
              Total: ₹{totalAmount.toFixed(2)}
            </Typography>
          </Stack>

          <Stack spacing={1}>
            {lineItems.map((item, idx) => {
              const isOpen = expandedId === item.local_id;
              const typeInfo = expenseTypeMap[item.expense_type];
              const billCount = totalBillCount(item);
              const hasErrors = !!errors[item.local_id];

              return (
                <Paper
                  key={item.local_id}
                  elevation={0}
                  sx={{
                    borderRadius: "4px",
                    border: hasErrors
                      ? "1.5px solid #dc2626"
                      : isOpen
                        ? `1.5px solid ${brand}`
                        : "1px solid #e5e7eb",
                    overflow: "hidden",
                  }}
                >
                  {/* Compact summary row — always visible */}
                  <Box
                    onClick={() => toggleExpand(item.local_id)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.5,
                      py: 1,
                      cursor: "pointer",
                      bgcolor: isOpen ? alpha(brand, 0.04) : "#fff",
                      "&:hover": {
                        bgcolor: isOpen ? alpha(brand, 0.06) : "#f8fafc",
                      },
                    }}
                  >
                    <Chip
                      label={idx + 1}
                      size="small"
                      sx={{
                        borderRadius: "4px",
                        bgcolor: alpha(brand, 0.1),
                        color: brand,
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        height: 20,
                        minWidth: 20,
                      }}
                    />

                    {typeInfo ? (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        sx={{ minWidth: 130 }}
                      >
                        {typeInfo.icon}
                        <Typography
                          sx={{
                            fontSize: "0.78rem",
                            fontWeight: 600,
                            color: "#111827",
                          }}
                        >
                          {typeInfo.label}
                        </Typography>
                      </Stack>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          color: "#9ca3af",
                          minWidth: 130,
                        }}
                      >
                        Select type...
                      </Typography>
                    )}

                    {item.vendor_name && (
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          color: "#6b7280",
                          flex: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.vendor_name}
                      </Typography>
                    )}

                    {!item.vendor_name && <Box sx={{ flex: 1 }} />}

                    {billCount > 0 && (
                      <Tooltip title={`${billCount} document(s) attached`}>
                        <Stack
                          direction="row"
                          spacing={0.3}
                          alignItems="center"
                          sx={{ color: "#10b981" }}
                        >
                          <AttachFileIcon sx={{ fontSize: 14 }} />
                          <Typography
                            sx={{ fontSize: "0.68rem", fontWeight: 600 }}
                          >
                            {billCount}
                          </Typography>
                        </Stack>
                      </Tooltip>
                    )}

                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        color: "#111827",
                        minWidth: 70,
                        textAlign: "right",
                      }}
                    >
                      {item.amount
                        ? `₹${parseFloat(item.amount).toFixed(2)}`
                        : "—"}
                    </Typography>

                    {hasErrors && (
                      <Chip
                        label="!"
                        size="small"
                        sx={{
                          bgcolor: alpha("#dc2626", 0.1),
                          color: "#dc2626",
                          fontWeight: 700,
                          height: 18,
                          minWidth: 18,
                          fontSize: "0.65rem",
                        }}
                      />
                    )}

                    {lineItems.length > 1 && (
                      <Tooltip title="Remove this expense">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeLineItem(item.local_id);
                          }}
                          sx={{
                            p: 0.4,
                            color: "#dc2626",
                            "&:hover": { bgcolor: alpha("#dc2626", 0.08) },
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    )}

                    <IconButton size="small" sx={{ p: 0.4, color: "#6b7280" }}>
                      {isOpen ? (
                        <ExpandLessIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <ExpandMoreIcon sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </Box>

                  {/* Expanded editor */}
                  <Collapse in={isOpen} timeout={150} unmountOnExit>
                    <Box
                      sx={{
                        px: 1.5,
                        pb: 1.5,
                        pt: 0.5,
                        borderTop: "1px solid #f1f5f9",
                      }}
                    >
                      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            fullWidth
                            select
                            required
                            label="Expense Type"
                            name="expense_type"
                            size="small"
                            value={item.expense_type}
                            onChange={(e) =>
                              handleLineItemChange(item.local_id, e)
                            }
                            error={!!errors[item.local_id]?.expense_type}
                            helperText={errors[item.local_id]?.expense_type}
                            sx={textFieldSx}
                          >
                            {expenseTypes.map((option) => (
                              <MenuItem
                                key={option.value}
                                value={option.value}
                                sx={{ fontSize: "0.75rem" }}
                              >
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  {option.icon}
                                  <span>{option.label}</span>
                                </Stack>
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            fullWidth
                            label="Vendor Name"
                            name="vendor_name"
                            size="small"
                            value={item.vendor_name}
                            onChange={(e) =>
                              handleLineItemChange(item.local_id, e)
                            }
                            sx={textFieldSx}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            fullWidth
                            label="Receipt / Bill No."
                            name="receipt_no"
                            size="small"
                            value={item.receipt_no}
                            onChange={(e) =>
                              handleLineItemChange(item.local_id, e)
                            }
                            sx={textFieldSx}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            fullWidth
                            type="number"
                            label="KM Reading"
                            name="km_reading"
                            size="small"
                            placeholder="Odometer"
                            value={item.km_reading}
                            onChange={(e) =>
                              handleLineItemChange(item.local_id, e)
                            }
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Typography
                                    sx={{
                                      fontSize: "0.72rem",
                                      color: "#9ca3af",
                                    }}
                                  >
                                    km
                                  </Typography>
                                </InputAdornment>
                              ),
                            }}
                            sx={textFieldSx}
                          />
                        </Grid>
                      </Grid>

                      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            fullWidth
                            select
                            label="Payment Mode"
                            name="payment_mode"
                            size="small"
                            value={item.payment_mode}
                            onChange={(e) =>
                              handleLineItemChange(item.local_id, e)
                            }
                            sx={textFieldSx}
                          >
                            {paymentModes.map((mode) => (
                              <MenuItem
                                key={mode}
                                value={mode}
                                sx={{ fontSize: "0.75rem" }}
                              >
                                {mode}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            fullWidth
                            required
                            type="number"
                            label="Amount"
                            name="amount"
                            size="small"
                            value={item.amount}
                            onChange={(e) =>
                              handleLineItemChange(item.local_id, e)
                            }
                            error={!!errors[item.local_id]?.amount}
                            helperText={errors[item.local_id]?.amount}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Typography
                                    sx={{
                                      fontSize: "0.78rem",
                                      color: "#6b7280",
                                    }}
                                  >
                                    ₹
                                  </Typography>
                                </InputAdornment>
                              ),
                            }}
                            sx={textFieldSx}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={6}>
                          <TextField
                            fullWidth
                            label="Description"
                            name="description"
                            size="small"
                            placeholder="Describe this expense..."
                            value={item.description}
                            onChange={(e) =>
                              handleLineItemChange(item.local_id, e)
                            }
                            sx={textFieldSx}
                          />
                        </Grid>
                      </Grid>

                      {(item.expense_type === "service" ||
                        item.expense_type === "repair") && (
                        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              fullWidth
                              type="number"
                              label="Next Service KM"
                              name="next_service_km"
                              size="small"
                              value={item.next_service_km}
                              onChange={(e) =>
                                handleLineItemChange(item.local_id, e)
                              }
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <Typography
                                      sx={{
                                        fontSize: "0.72rem",
                                        color: "#9ca3af",
                                      }}
                                    >
                                      km
                                    </Typography>
                                  </InputAdornment>
                                ),
                              }}
                              sx={textFieldSx}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              fullWidth
                              type="date"
                              label="Next Service Date"
                              name="next_service_date"
                              size="small"
                              InputLabelProps={{ shrink: true }}
                              value={item.next_service_date}
                              onChange={(e) =>
                                handleLineItemChange(item.local_id, e)
                              }
                              sx={textFieldSx}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              fullWidth
                              label="Part(s) Replaced"
                              name="part_replaced"
                              size="small"
                              placeholder="e.g. Clutch plate"
                              value={item.part_replaced}
                              onChange={(e) =>
                                handleLineItemChange(item.local_id, e)
                              }
                              sx={textFieldSx}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              fullWidth
                              type="number"
                              label="Labour Charge"
                              name="labour_charge"
                              size="small"
                              value={item.labour_charge}
                              onChange={(e) =>
                                handleLineItemChange(item.local_id, e)
                              }
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Typography
                                      sx={{
                                        fontSize: "0.78rem",
                                        color: "#6b7280",
                                      }}
                                    >
                                      ₹
                                    </Typography>
                                  </InputAdornment>
                                ),
                              }}
                              sx={textFieldSx}
                            />
                          </Grid>
                        </Grid>
                      )}

                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={6}>
                          <DocumentCard
                            title="Service Bill"
                            type="service"
                            lineItemId={item.local_id}
                            existing={item.existingServiceBills}
                            newBills={item.serviceBills}
                            onUpload={handleLineItemFileUpload}
                            onRemove={handleLineItemRemoveFile}
                            onView={handleViewFile}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <DocumentCard
                            title="Expense Bill"
                            type="expense"
                            lineItemId={item.local_id}
                            existing={item.existingExpenseBills}
                            newBills={item.expenseBills}
                            onUpload={handleLineItemFileUpload}
                            onRemove={handleLineItemRemoveFile}
                            onView={handleViewFile}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Collapse>
                </Paper>
              );
            })}
          </Stack>

          <Button
            onClick={addLineItem}
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: "4px",
              textTransform: "none",
              fontSize: "0.75rem",
              mt: 1.5,
              mb: 2,
              px: 2,
              py: 0.9,
              color: brand,
              border: "1px dashed #c7d2fe",
              bgcolor: alpha(brand, 0.04),
              boxShadow: "none",
              "&:hover": {
                borderColor: brand,
                bgcolor: alpha(brand, 0.09),
                boxShadow: "none",
              },
            }}
          >
            Add Another Expense
          </Button>

          {/* ── Action Buttons ── */}
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent="flex-end"
            sx={{ mt: 3 }}
          >
            <Button
              startIcon={<ClearIcon sx={{ fontSize: 15 }} />}
              onClick={handleClearAll}
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "0.75rem",
                px: 2.5,
                py: 0.9,
                bgcolor: alpha("#dc2626", 0.07),
                color: "#dc2626",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: alpha("#dc2626", 0.14),
                  boxShadow: "none",
                },
              }}
            >
              Clear All
            </Button>

            <Button
              variant="contained"
              startIcon={
                loading ? (
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                ) : (
                  <SaveIcon sx={{ fontSize: 15 }} />
                )
              }
              disabled={loading}
              onClick={handleSubmit}
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "0.75rem",
                px: 2.5,
                py: 0.9,
                bgcolor: brand,
                boxShadow: "none",
                "&:hover": { bgcolor: "#4338ca", boxShadow: "none" },
                "&.Mui-disabled": { bgcolor: alpha(brand, 0.5), color: "#fff" },
              }}
            >
              {loading
                ? "Saving..."
                : isEditMode
                  ? "Update Expense"
                  : "Save Expense"}
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ top: "80px !important" }}
      >
        <Alert
          severity={snackbar.severity}
          sx={{ borderRadius: "4px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExpenseForm;
